import React from 'react';
import PropTypes from 'prop-types';
import RehypeReact from 'rehype-react';
import GatsbyLink from 'gatsby-link';
import GatsbyImage from 'gatsby-image';
import MediaQueries from 'react-responsive';

import { InlineGrid } from '@marvelapp/marvel-ui/grid';
import Align from '@marvelapp/marvel-ui/align';
import Button from '@marvelapp/marvel-ui/button';
import Heading from '@marvelapp/marvel-ui/heading';
import MarvelLink from '@marvelapp/marvel-ui/link';
import Paragraph from '@marvelapp/marvel-ui/paragraph';
import Spacing from '@marvelapp/marvel-ui/spacing';

import LeftCol from 'marketing-website/components/modules/Integrations/LeftCol';
import Line from 'marketing-website/components/modules/Line';
import Page from 'marketing-website/components/modules/Page';
import RightCol from 'marketing-website/components/modules/Integrations/RightCol';
import Video from 'marketing-website/components/modules/Integrations/Video';
import Wrapper from 'marketing-website/components/modules/Integrations/Wrapper';

import metaData from 'marketing-website/data/metadata.js';

import markdownComponents from 'marketing-website/components/markdown';
import propTypeEmptyString from 'marketing-website/utils/customPropTypesCheck.js';

/* eslint-enable react/prop-types */
export default function IntegrationDetailTemplate({ data }) {
  if (!data || !data.markdownRemark) return null;

  const { markdownRemark } = data;
  const { frontmatter, htmlAst } = markdownRemark;
  const {
    buttonLink,
    buttonText,
    linkOne,
    linkTwo,
    thumbnail,
    page,
    title,
    videoId,
    linkThree,
    linktextOne,
    linktextTwo,
    linktextThree,
    instructionText,
  } = frontmatter;

  const renderAst = new RehypeReact({
    createElement: React.createElement,
    components: markdownComponents,
  }).Compiler;

  const pageProps = {
    title: metaData.integrations[page].title,
    meta: [
      { name: 'description', content: metaData.integrations[page].description },
      { name: 'keywords', content: metaData.integrations[page].keywords },
    ],
    header: true,
    footer: true,
  };

  return (
    <Page {...pageProps}>
      <Wrapper>
        <LeftCol>
          <MediaQueries minWidth={770}>
            <div>
              <Spacing type="margin" size="m" position="bottom">
                <GatsbyImage
                  resolutions={thumbnail.childImageSharp.resolutions}
                />
              </Spacing>
              <Spacing type="margin" size="m" position="bottom">
                <a href={buttonLink} className="display-block">
                  <Button size="large" fullWidth>
                    {buttonText}
                  </Button>
                </a>
              </Spacing>
              {(linktextOne || linktextTwo || linkThree) && (
                <div>
                  <Line spacing="m" />
                  <Spacing type="margin" size="m" position="bottom">
                    <Spacing type="margin" size="s" position="bottom">
                      <Heading tag="h3" weight="bold" size="l">
                        Relevant links
                      </Heading>
                    </Spacing>
                    {linktextOne && linkOne && (
                      <Spacing type="margin" size="xs" position="bottom">
                        <MarvelLink url={linkOne} type="blue">
                          <span className="fontSize-m">{linktextOne}</span>
                        </MarvelLink>
                      </Spacing>
                    )}
                    {linktextTwo && linkTwo && (
                      <Spacing type="margin" size="xs" position="bottom">
                        <MarvelLink url={linkTwo} type="blue">
                          <span className="fontSize-m">{linktextTwo}</span>
                        </MarvelLink>
                      </Spacing>
                    )}
                    {linkThree && linktextThree && (
                      <MarvelLink url={linkThree} type="blue">
                        <span className="fontSize-m">{linktextThree}</span>
                      </MarvelLink>
                    )}
                  </Spacing>
                </div>
              )}
              {videoId && (
                <div>
                  <Line spacing="m" />
                  <Spacing type="margin" size="m" position="bottom">
                    <Heading tag="h3" weight="bold" size="l">
                      {instructionText}
                    </Heading>
                  </Spacing>
                  <Video id={videoId} />
                </div>
              )}
            </div>
          </MediaQueries>
        </LeftCol>
        <RightCol>
          <MediaQueries maxWidth={769}>
            <Spacing type="margin" size="l" position="bottom">
              <Align type="center">
                <GatsbyImage
                  resolutions={thumbnail.childImageSharp.resolutions}
                />
                <Spacing type="margin" size="m" position="topBottom">
                  <a href={buttonLink}>
                    <Button size="large" minWidth={260}>
                      {buttonText}
                    </Button>
                  </a>
                </Spacing>
              </Align>
              <Line spacing="m" />
            </Spacing>
          </MediaQueries>
          <Spacing type="margin" size="l" sizeM="xl" position="bottom">
            <InlineGrid size="xxs">
              <GatsbyLink to="/features/integrations">
                <Paragraph size="m">
                  <MarvelLink type="blue">
                    <span className="fontWeight-5">All Integrations</span>
                  </MarvelLink>
                </Paragraph>
              </GatsbyLink>
              <Paragraph size="m">
                <span className="fontWeight-5">/</span>
              </Paragraph>
              <Paragraph size="m">
                <span className="fontWeight-5">{title}</span>
              </Paragraph>
            </InlineGrid>
          </Spacing>
          <Spacing type="margin" size="l" position="bottom">
            <Heading tag="h1" weight="bold" size="xxxl">
              {title}
            </Heading>
          </Spacing>

          {renderAst(htmlAst)}
        </RightCol>
      </Wrapper>
    </Page>
  );
}

IntegrationDetailTemplate.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      htmlAst: PropTypes.object,
      frontmatter: PropTypes.shape({
        buttonLink: PropTypes.string,
        linktextOne: PropTypes.string,
        linktextTwo: PropTypes.string,
        linktextThree: PropTypes.string,
        buttonText: PropTypes.string,
        linkOne: PropTypes.string,
        linkTwo: PropTypes.string,
        linkThree: PropTypes.string,
        instructionText: PropTypes.string,
        title: PropTypes.string,
        thumbnail: PropTypes.object,
        page: propTypeEmptyString,
        videoId: PropTypes.string,
      }),
    }),
  }),
};

/* eslint-disable no-undef */
export const integrationQuery = graphql`
  query IntegrationByPath($path: String!) {
    markdownRemark(frontmatter: { path: { eq: $path } }) {
      htmlAst
      frontmatter {
        linktextOne
        linktextThree
        buttonLink
        buttonText
        linkOne
        linkTwo
        linkThree
        page
        path
        thumbnail {
          childImageSharp {
            resolutions(width: 260, height: 260, quality: 95) {
              ...GatsbyImageSharpResolutions
            }
          }
        }
        title
        videoId
        linktextTwo
        instructionText
      }
    }
  }
`;
/* eslint-enable no-undef */



// WEBPACK FOOTER //
// ./src/templates/integration.js